import { PageClassSelector, useTeacherWorkspace } from '../../../components/layout/TeacherWorkspace';
import React, { useEffect, useMemo, useState } from 'react';
import { preferredTeacherSection } from '../../../services/teacherSection';
import { Link } from 'react-router-dom';
import TeacherNav from '../../../components/TeacherNav';
import { OptionalAppShell } from '../../../components/layout/AppShell';
import { teacherV2NavItems } from '../../../components/layout/v2NavItems';
import { isUiRedesignPageEnabled } from '../../../config/uiRedesign';
import { reportsApi, sectionsApi } from '../../../services/apiV2';
import { Section } from '../../../types/v2';

interface SectionStudent {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}

interface StepReport {
  lesson_step: number;
  lesson_step_title: string;
  step_order: number;
  stage_tag: string;
  step_kind: string;
  signal_submitted: boolean;
  has_reasoning: boolean;
  confidence_level: number | null;
  is_correct: boolean | null;
  evidence_submitted: boolean;
  evidence_status: string;
  signal_latest_text?: string;
  evidence_latest_text?: string;
  signal_attempts?: Array<{
    id: number;
    submitted_at: string;
    response?: Record<string, any>;
    preview_text?: string;
  }>;
  evidence_attempts?: Array<{
    id: number;
    submitted_at: string;
    status?: string;
    evidence_type?: string;
    content?: Record<string, any>;
    preview_text?: string;
  }>;
}

interface RunReport {
  id: number;
  lesson_run: number;
  lesson_plan_title: string;
  hook_title: string;
  run_started_at: string;
  run_ended_at: string | null;
  run_status: string;
  last_activity_at: string | null;
  steps_total: number;
  signals_submitted_count: number;
  evidence_submitted_count: number;
  overlay_reply_count: number;
  intervention_count: number;
  acceleration_count: number;
  missing_reasoning_count: number;
  missing_confidence_count: number;
  high_confidence_count: number;
  low_confidence_count: number;
  engagement_score: string | number;
  step_reports: StepReport[];
}

interface DailyReport {
  id: number;
  report_date: string;
  section_name: string;
  hook_title: string;
  runs_count: number;
  signals_submitted_count: number;
  evidence_submitted_count: number;
  intervention_count: number;
  acceleration_count: number;
  engagement_score_avg: string | number;
}

interface RunsSummary {
  runs_count: number;
  signals_submitted_count: number;
  evidence_submitted_count: number;
  intervention_count: number;
  acceleration_count: number;
  missing_reasoning_count: number;
  missing_confidence_count: number;
  high_confidence_count: number;
  low_confidence_count: number;
  engagement_score_avg: number;
}

interface CompletionSourceCounts {
  assigned: number;
  completed: number;
  not_completed: number;
  completion_rate: number;
}

interface CompletionPeriod {
  key: string;
  start_date: string;
  end_date: string;
  label: string;
}

interface CompletionPeriodRow {
  period_key: string;
  start_date: string;
  end_date: string;
  label: string;
  live: CompletionSourceCounts;
  makeup: CompletionSourceCounts;
  external?: CompletionSourceCounts;
  combined: CompletionSourceCounts;
  missing_items?: Array<Record<string, any>>;
}

interface CompletionStudentRow {
  student_id: number;
  student_name: string;
  periods: CompletionPeriodRow[];
  totals: {
    live: CompletionSourceCounts;
    makeup: CompletionSourceCounts;
    external?: CompletionSourceCounts;
    combined: CompletionSourceCounts;
    missing_items_count?: number;
  };
}

interface CompletionSummary {
  students_total: number;
  periods_total: number;
  live: CompletionSourceCounts;
  makeup: CompletionSourceCounts;
  external?: CompletionSourceCounts;
  combined: CompletionSourceCounts;
}

interface SectionCompletionPayload {
  section_id: number;
  section_name: string;
  from: string;
  to: string;
  grain: 'day' | 'week';
  include_live: boolean;
  include_makeup: boolean;
  include_external: boolean;
  periods: CompletionPeriod[];
  students: CompletionStudentRow[];
  summary: CompletionSummary;
}

type ReportView = 'section_completion' | 'student_activity';
type SectionVisibility = 'current' | 'archived' | 'all';

const formatDateTime = (value?: string | null) => {
  if (!value) return 'n/a';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const formatDate = (value?: string | null) => {
  if (!value) return 'n/a';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const normalizeArray = (data: any) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const completionCellClass = (rate: number, assigned: number) => {
  if (assigned <= 0) return 'bg-gray-50 border-gray-100';
  if (rate >= 99.99) return 'bg-emerald-50 border-emerald-200';
  if (rate >= 70) return 'bg-amber-50 border-amber-200';
  return 'bg-rose-50 border-rose-200';
};

const formatStatusLabel = (value?: string | null) => {
  const normalized = String(value || '').trim();
  if (!normalized) return '';
  return normalized
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const missingItemKindLabel = (kind?: string) => {
  const normalized = String(kind || '').trim().toLowerCase();
  if (normalized === 'live_run') return 'Live';
  if (normalized === 'makeup_assignment') return 'Make-up';
  if (normalized === 'external_task') return 'External';
  return 'Item';
};

const normalizeMissingStatus = (status?: string) => {
  const normalized = String(status || '').trim().toLowerCase();
  if (!normalized) return 'not_started';
  return normalized;
};

const missingStatusLabel = (status?: string) => {
  const normalized = normalizeMissingStatus(status);
  if (normalized === 'in_progress') return 'In Progress';
  if (normalized === 'not_started') return 'Not Started';
  if (normalized === 'completed') return 'Completed';
  if (normalized === 'not_completed') return 'Not Completed';
  return formatStatusLabel(normalized);
};

const missingStatusPillClass = (status?: string) => {
  const normalized = normalizeMissingStatus(status);
  if (normalized === 'in_progress') return 'bg-amber-100 text-amber-800';
  if (normalized === 'completed') return 'bg-emerald-100 text-emerald-800';
  return 'bg-rose-100 text-rose-800';
};

const missingKindSortOrder = (kind?: string) => {
  const normalized = String(kind || '').trim().toLowerCase();
  if (normalized === 'makeup_assignment') return 0;
  if (normalized === 'live_run') return 1;
  if (normalized === 'external_task') return 2;
  return 9;
};

const missingStatusSortOrder = (status?: string) => {
  const normalized = normalizeMissingStatus(status);
  if (normalized === 'in_progress') return 0;
  if (normalized === 'not_started') return 1;
  if (normalized === 'not_completed') return 2;
  return 9;
};

const sortMissingItems = (items?: Array<Record<string, any>>) => (
  [...(items || [])].sort((left, right) => {
    const byStatus = missingStatusSortOrder(left?.status) - missingStatusSortOrder(right?.status);
    if (byStatus !== 0) return byStatus;

    const leftDue = String(left?.due_at || '').trim();
    const rightDue = String(right?.due_at || '').trim();
    if (leftDue && rightDue && leftDue !== rightDue) {
      return leftDue.localeCompare(rightDue);
    }
    if (leftDue && !rightDue) return -1;
    if (!leftDue && rightDue) return 1;

    const byKind = missingKindSortOrder(left?.kind) - missingKindSortOrder(right?.kind);
    if (byKind !== 0) return byKind;

    const leftTitle = String(left?.title || '').toLowerCase();
    const rightTitle = String(right?.title || '').toLowerCase();
    return leftTitle.localeCompare(rightTitle);
  })
);

export const V2TeacherReports: React.FC = () => {
  const redesignEnabled = isUiRedesignPageEnabled('v2_teacher_reports');
  const [sections, setSections] = useState<Section[]>([]);
  const [students, setStudents] = useState<SectionStudent[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<number | null>(null);
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);
  const [reportView, setReportView] = useState<ReportView>('section_completion');
  const workspace = useTeacherWorkspace();
  const [sectionVisibility, setSectionVisibility] = useState<SectionVisibility>('current');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [includeSteps, setIncludeSteps] = useState(false);
  const [completionGrain, setCompletionGrain] = useState<'day' | 'week'>('day');
  const [completionIncludeLive, setCompletionIncludeLive] = useState(true);
  const [completionIncludeMakeup, setCompletionIncludeMakeup] = useState(true);
  const [completionIncludeExternal, setCompletionIncludeExternal] = useState(false);
  const [completionIncludeItems, setCompletionIncludeItems] = useState(true);
  const [completionOnlyIncomplete, setCompletionOnlyIncomplete] = useState(true);

  const [runReports, setRunReports] = useState<RunReport[]>([]);
  const [dailyReports, setDailyReports] = useState<DailyReport[]>([]);
  const [runsSummary, setRunsSummary] = useState<RunsSummary | null>(null);
  const [dailyDays, setDailyDays] = useState(0);
  const [completionData, setCompletionData] = useState<SectionCompletionPayload | null>(null);

  const [loadingSections, setLoadingSections] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [loadingCompletion, setLoadingCompletion] = useState(false);
  const [error, setError] = useState('');

  const visibleSections = useMemo(() => sections.filter((section) => {
    if (workspace) return String(section.id) === workspace.selected;
    if (sectionVisibility === 'current') return section.is_active !== false;
    if (sectionVisibility === 'archived') return section.is_active === false;
    return true;
  }), [sectionVisibility, sections, workspace]);

  const selectedStudent = useMemo(
    () => students.find((student) => student.id === selectedStudentId) || null,
    [students, selectedStudentId],
  );

  const completionStudents = useMemo(() => {
    const rows = Array.isArray(completionData?.students) ? (completionData?.students || []) : [];
    if (!completionOnlyIncomplete) return rows;
    return rows.filter((student) => Number(student?.totals?.combined?.not_completed || 0) > 0);
  }, [completionData, completionOnlyIncomplete]);

  const hasParticipation = (report: RunReport) =>
    report.signals_submitted_count > 0 ||
    report.evidence_submitted_count > 0 ||
    report.overlay_reply_count > 0;

  const renderSignalTextCell = (step: StepReport) => {
    const attempts = Array.isArray(step.signal_attempts) ? step.signal_attempts : [];
    if (!attempts.length) {
      return <span className="text-gray-400">—</span>;
    }
    const latest = attempts[0];
    const latestText = String(step.signal_latest_text || latest?.preview_text || '').trim() || '—';
    return (
      <div className="space-y-1">
        <div className="whitespace-pre-wrap break-words text-gray-700">{latestText}</div>
        <div className="text-[11px] text-gray-500">
          {formatDateTime(latest?.submitted_at)} · {attempts.length} attempt{attempts.length === 1 ? '' : 's'}
        </div>
        {attempts.length > 1 ? (
          <details className="text-[11px] text-gray-600">
            <summary className="cursor-pointer">Show older attempts</summary>
            <div className="mt-1 space-y-1">
              {attempts.slice(1).map((attempt) => {
                const text = String(attempt?.preview_text || '').trim() || '—';
                return (
                  <div key={`signal-attempt-${step.lesson_step}-${attempt.id}`} className="rounded border border-gray-100 bg-gray-50 p-2">
                    <div className="text-gray-500">{formatDateTime(attempt?.submitted_at)}</div>
                    <div className="whitespace-pre-wrap break-words text-gray-700">{text}</div>
                  </div>
                );
              })}
            </div>
          </details>
        ) : null}
      </div>
    );
  };

  const renderEvidenceTextCell = (step: StepReport) => {
    const attempts = Array.isArray(step.evidence_attempts) ? step.evidence_attempts : [];
    if (!attempts.length) {
      return <span className="text-gray-400">—</span>;
    }
    const latest = attempts[0];
    const latestText = String(step.evidence_latest_text || latest?.preview_text || '').trim() || '—';
    return (
      <div className="space-y-1">
        <div className="whitespace-pre-wrap break-words text-gray-700">{latestText}</div>
        <div className="text-[11px] text-gray-500">
          {formatStatusLabel(latest?.status || step.evidence_status || 'submitted')} · {formatDateTime(latest?.submitted_at)} · {attempts.length} attempt{attempts.length === 1 ? '' : 's'}
        </div>
        {attempts.length > 1 ? (
          <details className="text-[11px] text-gray-600">
            <summary className="cursor-pointer">Show older attempts</summary>
            <div className="mt-1 space-y-1">
              {attempts.slice(1).map((attempt) => {
                const text = String(attempt?.preview_text || '').trim() || '—';
                return (
                  <div key={`evidence-attempt-${step.lesson_step}-${attempt.id}`} className="rounded border border-gray-100 bg-gray-50 p-2">
                    <div className="text-gray-500">
                      {formatStatusLabel(attempt?.status || 'submitted')} · {formatDateTime(attempt?.submitted_at)}
                    </div>
                    <div className="whitespace-pre-wrap break-words text-gray-700">{text}</div>
                  </div>
                );
              })}
            </div>
          </details>
        ) : null}
      </div>
    );
  };

  const assignedRuns = runReports.length;
  const participatedRuns = useMemo(
    () => runReports.filter((report) => hasParticipation(report)).length,
    [runReports],
  );
  const participationRate = assignedRuns > 0 ? (participatedRuns / assignedRuns) * 100 : 0;
  const participatedByDate = useMemo(() => {
    const map: Record<string, number> = {};
    runReports.forEach((report) => {
      if (!hasParticipation(report)) return;
      const day = report.run_started_at ? String(report.run_started_at).slice(0, 10) : '';
      if (!day) return;
      map[day] = (map[day] || 0) + 1;
    });
    return map;
  }, [runReports]);

  const loadSections = async () => {
    setLoadingSections(true);
    try {
      const response = await sectionsApi.list();
      const sectionRows = normalizeArray(response.data);
      setSections(sectionRows);
      const currentRows = sectionRows.filter((section: Section) => section.is_active !== false);
      if (currentRows.length > 0) {
        setSelectedSectionId((current) => current || preferredTeacherSection(sectionRows));
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load sections.');
    } finally {
      setLoadingSections(false);
    }
  };

  const loadStudents = async (sectionId: number) => {
    setLoadingStudents(true);
    try {
      const response = await sectionsApi.students(sectionId);
      const studentRows = Array.isArray(response.data?.students) ? response.data.students : [];
      setStudents(studentRows);
      setSelectedStudentId((current) => {
        if (!studentRows.length) return null;
        if (current && studentRows.some((student: SectionStudent) => student.id === current)) return current;
        return studentRows[0].id;
      });
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load students for section.');
      setStudents([]);
      setSelectedStudentId(null);
    } finally {
      setLoadingStudents(false);
    }
  };

  const loadReports = async (refresh = false) => {
    if (!selectedStudentId || !selectedSectionId) return;

    setLoadingReports(true);
    setError('');
    try {
      const paramsBase = {
        section_id: selectedSectionId,
        from: fromDate || undefined,
        to: toDate || undefined,
      };
      const [runsResponse, dailyResponse] = await Promise.all([
        reportsApi.studentRuns(selectedStudentId, {
          ...paramsBase,
          include_steps: includeSteps,
          refresh,
        }),
        reportsApi.studentDaily(selectedStudentId, {
          ...paramsBase,
          refresh,
        }),
      ]);
      setRunReports(Array.isArray(runsResponse.data?.reports) ? runsResponse.data.reports : []);
      setDailyReports(Array.isArray(dailyResponse.data?.reports) ? dailyResponse.data.reports : []);
      setRunsSummary(runsResponse.data?.summary || null);
      setDailyDays(Number(dailyResponse.data?.summary?.days || 0));
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load reports.');
    } finally {
      setLoadingReports(false);
    }
  };

  const loadSectionCompletion = async () => {
    if (!selectedSectionId) return;

    setLoadingCompletion(true);
    setError('');
    try {
      const response = await reportsApi.sectionCompletion(selectedSectionId, {
        from: fromDate || undefined,
        to: toDate || undefined,
        grain: completionGrain,
        include_live: completionIncludeLive,
        include_makeup: completionIncludeMakeup,
        include_external: completionIncludeExternal,
        include_items: completionIncludeItems,
      });
      setCompletionData((response?.data || null) as SectionCompletionPayload | null);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load section completion report.');
      setCompletionData(null);
    } finally {
      setLoadingCompletion(false);
    }
  };

  useEffect(() => {
    loadSections();
  }, []);

  useEffect(() => {
    if (!visibleSections.length) {
      setSelectedSectionId(null);
      setSelectedStudentId(null);
      return;
    }
    if (!selectedSectionId || !visibleSections.some((section) => section.id === selectedSectionId)) {
      setSelectedSectionId(visibleSections[0].id);
    }
  }, [selectedSectionId, visibleSections]);

  useEffect(() => {
    if (!selectedSectionId) return;
    setError('');
    setRunReports([]);
    setDailyReports([]);
    setRunsSummary(null);
    setDailyDays(0);
    setCompletionData(null);
    loadStudents(selectedSectionId);
  }, [selectedSectionId]);

  useEffect(() => {
    if (reportView !== 'student_activity') return;
    if (!selectedStudentId || !selectedSectionId) return;
    loadReports(false);
  }, [reportView, selectedStudentId, selectedSectionId, includeSteps]);

  useEffect(() => {
    if (reportView !== 'section_completion') return;
    if (!selectedSectionId) return;
    loadSectionCompletion();
  }, [
    reportView,
    selectedSectionId,
    fromDate,
    toDate,
    completionGrain,
    completionIncludeLive,
    completionIncludeMakeup,
    completionIncludeExternal,
    completionIncludeItems,
  ]);

  return (
    <OptionalAppShell enabled={redesignEnabled} role="teacher" navItems={teacherV2NavItems('reports')} title="Reports">
      {!redesignEnabled && <TeacherNav activeTab="reports" />}
      <div className={redesignEnabled ? 'max-w-6xl mx-auto space-y-4' : 'max-w-6xl mx-auto p-6'}>
        <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
        <p className="text-sm text-gray-500 mt-1">Choose a report type, then filter by section and date range.</p>

        {error && (
          <div className="mt-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-2">
          <button
            type="button"
            className={`rounded px-3 py-1.5 text-sm font-medium ${
              reportView === 'section_completion'
                ? 'bg-indigo-600 text-white'
                : 'border border-gray-300 bg-white text-gray-700'
            }`}
            onClick={() => setReportView('section_completion')}
          >
            Section Completion
          </button>
          <button
            type="button"
            className={`rounded px-3 py-1.5 text-sm font-medium ${
              reportView === 'student_activity'
                ? 'bg-indigo-600 text-white'
                : 'border border-gray-300 bg-white text-gray-700'
            }`}
            onClick={() => setReportView('student_activity')}
          >
            Student Activity
          </button>
        </div>

        <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
            <PageClassSelector><div>
              <label className="block text-sm text-gray-600">Section visibility</label>
              <select
                aria-label="Section visibility"
                className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={sectionVisibility}
                onChange={(event) => setSectionVisibility(event.target.value as SectionVisibility)}
                disabled={loadingSections}
              >
                <option value="current">Current sections</option>
                <option value="archived">Archived sections</option>
                <option value="all">All sections</option>
              </select>
            </div></PageClassSelector>
            <PageClassSelector><div>
              <label htmlFor="report-section" className="block text-sm text-gray-600">Section</label>
              <select
                id="report-section"
                className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={selectedSectionId || ''}
                onChange={(e) => setSelectedSectionId(e.target.value ? Number(e.target.value) : null)}
                disabled={loadingSections}
              >
                {visibleSections.map((section) => (
                  <option key={section.id} value={section.id}>
                    {section.name}{section.is_active === false ? ' (Archived)' : ''}
                  </option>
                ))}
              </select>
            </div></PageClassSelector>
            {reportView === 'student_activity' && (
              <div>
              <label htmlFor="report-student" className="block text-sm text-gray-600">Student</label>
              <select
                id="report-student"
                className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={selectedStudentId || ''}
                onChange={(e) => setSelectedStudentId(e.target.value ? Number(e.target.value) : null)}
                disabled={loadingStudents || students.length === 0}
              >
                {students.map((student) => (
                  <option key={student.id} value={student.id}>
                    {`${student.last_name}, ${student.first_name}`}
                  </option>
                ))}
              </select>
              {selectedStudentId && (
                <Link
                  to={`/v2/teacher/students/${selectedStudentId}/work${selectedSectionId ? `?section=${selectedSectionId}` : ''}`}
                  className="mt-1 inline-block text-xs font-medium text-indigo-700 hover:text-indigo-900 hover:underline"
                >
                  Open student work history
                </Link>
              )}
              </div>
            )}
            <div>
              <label htmlFor="report-from" className="block text-sm text-gray-600">From</label>
              <input
                id="report-from"
                type="date"
                className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="report-to" className="block text-sm text-gray-600">To</label>
              <input
                id="report-to"
                type="date"
                className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <button
                className="w-full rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
                onClick={() => {
                  if (reportView === 'student_activity') {
                    loadReports(true);
                    return;
                  }
                  loadSectionCompletion();
                }}
                disabled={
                  !selectedSectionId
                  || (reportView === 'student_activity' && (!selectedStudentId || loadingReports))
                  || (reportView === 'section_completion' && loadingCompletion)
                }
              >
                {reportView === 'student_activity'
                  ? (loadingReports ? 'Refreshing...' : 'Refresh Student Activity')
                  : (loadingCompletion ? 'Refreshing...' : 'Refresh Section Completion')}
              </button>
            </div>
          </div>

          {reportView === 'student_activity' && (
            <div className="mt-3 flex items-center gap-2">
              <input
                id="include-steps"
                type="checkbox"
                className="h-4 w-4"
                checked={includeSteps}
                onChange={(e) => setIncludeSteps(e.target.checked)}
              />
              <label htmlFor="include-steps" className="text-sm text-gray-700">
                Include step-level details
              </label>
            </div>
          )}

          {reportView === 'section_completion' && (
            <div className="mt-4 rounded border border-gray-200 bg-gray-50 p-3">
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-5">
              <div>
                <label className="block text-xs uppercase tracking-wide text-gray-600">Section Completion Grain</label>
                <select
                  className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  value={completionGrain}
                  onChange={(e) => setCompletionGrain((e.target.value === 'week' ? 'week' : 'day'))}
                >
                  <option value="day">Day</option>
                  <option value="week">Week</option>
                </select>
              </div>
              <label className="flex items-end gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={completionIncludeLive}
                  onChange={(e) => setCompletionIncludeLive(e.target.checked)}
                />
                Live lessons
              </label>
              <label className="flex items-end gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={completionIncludeMakeup}
                  onChange={(e) => setCompletionIncludeMakeup(e.target.checked)}
                />
                Assignment records (all purposes)
              </label>
              <label className="flex items-end gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={completionIncludeExternal}
                  onChange={(e) => setCompletionIncludeExternal(e.target.checked)}
                />
                External tasks
              </label>
              <label className="flex items-end gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={completionIncludeItems}
                  onChange={(e) => setCompletionIncludeItems(e.target.checked)}
                />
                Include missing items
              </label>
            </div>
            <p className="mt-3 text-xs text-gray-500">Changing these filters updates the report automatically.</p>
            </div>
          )}
        </div>

        {reportView === 'student_activity' && selectedStudent && (
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Runs Assigned</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">{assignedRuns}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Runs Participated</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">{participatedRuns}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Participation Rate</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">{participationRate.toFixed(1)}%</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Engagement Avg</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {runsSummary ? Number(runsSummary.engagement_score_avg || 0).toFixed(1) : '0.0'}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Signals</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">{runsSummary?.signals_submitted_count || 0}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Trend Days</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">{dailyDays}</p>
            </div>
          </div>
        )}

        {reportView === 'section_completion' && (
          <div className="mt-6 rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-4 py-3">
            <h2 className="text-sm font-semibold text-gray-900">Section Completion Matrix</h2>
            <p className="mt-1 text-xs text-gray-500">
              Cell format: <span className="font-medium">Completed / Assigned</span>. L = Live lessons, M = Assignment records (all purposes), E = External tasks.
            </p>
            <p className="mt-2 text-xs text-gray-600">Counts records, not unique work obligations. A lesson and its linked make-up can count separately here; Class Overview counts the original lesson once and accepts completed make-up work in its place. Assignments use their assigned date in the class timezone, including archived assignments; excused assignments are excluded. Daily Completion instead uses due date (or assigned date) and active assignments only. Completion is not proficiency.</p>
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                className={`rounded px-2.5 py-1 text-xs font-medium ${
                  completionOnlyIncomplete
                    ? 'bg-rose-600 text-white'
                    : 'border border-gray-300 bg-white text-gray-700'
                }`}
                onClick={() => setCompletionOnlyIncomplete((current) => !current)}
              >
                {completionOnlyIncomplete ? 'Showing Only Incomplete' : 'Only Incomplete'}
              </button>
              <span className="text-xs text-gray-500">
                Showing {completionStudents.length} of {completionData?.summary?.students_total || 0} students
              </span>
            </div>
            <p className="mt-1 text-xs text-gray-500">Summary totals cover the full section and selected sources. Only Incomplete filters the rows, not these totals.</p>
          </div>
          <div className="px-4 py-3">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">Combined Assigned</p>
                <p className="mt-1 text-lg font-semibold text-gray-900">
                  {completionData?.summary?.combined?.assigned || 0}
                </p>
              </div>
              <div className="rounded border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">Combined Completed</p>
                <p className="mt-1 text-lg font-semibold text-gray-900">
                  {completionData?.summary?.combined?.completed || 0}
                </p>
              </div>
              <div className="rounded border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">Combined Not Completed</p>
                <p className="mt-1 text-lg font-semibold text-gray-900">
                  {completionData?.summary?.combined?.not_completed || 0}
                </p>
              </div>
              <div className="rounded border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">Completion Rate</p>
                <p className="mt-1 text-lg font-semibold text-gray-900">
                  {completionData?.summary?.combined?.assigned ? `${Number(completionData.summary.combined.completion_rate || 0).toFixed(1)}%` : 'No assigned records'}
                </p>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto border-t border-gray-200">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="sticky left-0 z-10 bg-gray-50 px-3 py-2 text-left font-medium text-gray-700">Student</th>
                  {(completionData?.periods || []).map((period) => (
                    <th key={`completion-header-${period.key}`} className="min-w-[130px] px-3 py-2 text-left font-medium text-gray-700">
                      {period.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {completionStudents.map((student) => {
                  const periodMap = new Map(
                    (student.periods || []).map((periodRow) => [periodRow.period_key, periodRow]),
                  );
                  return (
                    <tr key={`completion-student-${student.student_id}`} className="align-top">
                      <td className="sticky left-0 z-10 border-r border-gray-100 bg-white px-3 py-2">
                        <Link
                          to={`/v2/teacher/students/${student.student_id}/work${selectedSectionId ? `?section=${selectedSectionId}` : ''}`}
                          className="font-medium text-indigo-700 hover:text-indigo-900 hover:underline"
                        >
                          {student.student_name}
                        </Link>
                        <div className="text-[11px] text-gray-500">
                          Total completed: {student.totals.combined.completed}/{student.totals.combined.assigned}
                        </div>
                      </td>
                      {(completionData?.periods || []).map((period) => {
                        const cell = periodMap.get(period.key);
                        const combined = cell?.combined || {
                          assigned: 0,
                          completed: 0,
                          not_completed: 0,
                          completion_rate: 0,
                        };
                        return (
                          <td key={`completion-cell-${student.student_id}-${period.key}`} className="px-3 py-2">
                            <div className={`rounded border p-2 ${completionCellClass(combined.completion_rate, combined.assigned)}`}>
                              <div className="text-sm font-semibold text-gray-900">
                                Completed {combined.completed}/{combined.assigned}
                              </div>
                              <div className="text-[11px] text-gray-600">
                                {combined.assigned ? `${Number(combined.completion_rate || 0).toFixed(0)}%` : 'No assigned records'}
                              </div>
                              <div className="mt-1 text-[11px] text-gray-600">
                                L {cell?.live.completed || 0}/{cell?.live.assigned || 0} | M {cell?.makeup.completed || 0}/{cell?.makeup.assigned || 0}
                              </div>
                              {completionIncludeExternal && (
                                <div className="text-[11px] text-gray-600">
                                  E {cell?.external?.completed || 0}/{cell?.external?.assigned || 0}
                                </div>
                              )}
                              {completionIncludeItems && (
                                <details className="mt-1 text-[11px] text-gray-600">
                                  <summary className="cursor-pointer">Missing: {(cell?.missing_items || []).length}</summary>
                                  <div className="mt-1 space-y-1">
                                    {sortMissingItems(cell?.missing_items).slice(0, 6).map((item, idx) => (
                                      <div
                                        key={`missing-item-${student.student_id}-${period.key}-${idx}`}
                                        className="rounded border border-gray-200 bg-white px-2 py-1"
                                      >
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                          <div className="font-medium text-gray-700">
                                            {missingItemKindLabel(item?.kind)}: {String(item?.title || 'Untitled')}
                                          </div>
                                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${missingStatusPillClass(item?.status)}`}>
                                            {missingStatusLabel(item?.status)}
                                          </span>
                                        </div>
                                        <div className="text-gray-500">
                                          {item?.due_at ? `Due ${formatDate(item?.due_at)}` : 'No due date'}
                                        </div>
                                      </div>
                                    ))}
                                    {(cell?.missing_items || []).length > 6 && (
                                      <div className="text-gray-500">
                                        +{(cell?.missing_items || []).length - 6} more
                                      </div>
                                    )}
                                  </div>
                                </details>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {!loadingCompletion && completionStudents.length === 0 && (
                  <tr>
                    <td
                      colSpan={Math.max(1, (completionData?.periods || []).length + 1)}
                      className="px-4 py-6 text-center text-sm text-gray-500"
                    >
                      {completionOnlyIncomplete
                        ? 'No incomplete students match the selected filters.'
                        : 'No section completion rows found for the selected filters.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {reportView === 'student_activity' && (
          <div className="mt-6 rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-4 py-3">
            <h2 className="text-sm font-semibold text-gray-900">Run History</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Run Start</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Lesson</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Status</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Participated</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Signals</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Evidence</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Missing</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Confidence</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Engagement</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {runReports.map((report) => (
                  <React.Fragment key={report.id}>
                    <tr className="align-top">
                      <td className="px-4 py-3 text-gray-700">
                        <div>{formatDateTime(report.run_started_at)}</div>
                        <div className="text-xs text-gray-500">Last active: {formatDateTime(report.last_activity_at)}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <div className="font-medium text-gray-900">{report.lesson_plan_title}</div>
                        <div className="text-xs text-gray-500">{report.hook_title}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{formatStatusLabel(report.run_status)}</td>
                      <td className="px-4 py-3 text-gray-700">{hasParticipation(report) ? 'Yes' : 'No'}</td>
                      <td className="px-4 py-3 text-gray-700">{`${report.signals_submitted_count}/${report.steps_total}`}</td>
                      <td className="px-4 py-3 text-gray-700">{report.evidence_submitted_count}</td>
                      <td className="px-4 py-3 text-gray-700">{`R:${report.missing_reasoning_count} C:${report.missing_confidence_count}`}</td>
                      <td className="px-4 py-3 text-gray-700">{`H:${report.high_confidence_count} L:${report.low_confidence_count}`}</td>
                      <td className="px-4 py-3 font-semibold text-gray-900">{Number(report.engagement_score || 0).toFixed(1)}</td>
                    </tr>
                    {includeSteps && report.step_reports.length > 0 && (
                      <tr>
                        <td className="px-4 py-3 bg-gray-50" colSpan={9}>
                          <div className="overflow-x-auto rounded border border-gray-200">
                            <table className="min-w-full divide-y divide-gray-200 text-xs">
                              <thead className="bg-white">
                                <tr>
                                  <th className="px-3 py-2 text-left font-medium text-gray-600">Step</th>
                                  <th className="px-3 py-2 text-left font-medium text-gray-600">Signal</th>
                                  <th className="px-3 py-2 text-left font-medium text-gray-600">Reasoning</th>
                                  <th className="px-3 py-2 text-left font-medium text-gray-600">Confidence</th>
                                  <th className="px-3 py-2 text-left font-medium text-gray-600">Correct</th>
                                  <th className="px-3 py-2 text-left font-medium text-gray-600">Evidence</th>
                                  <th className="px-3 py-2 text-left font-medium text-gray-600">Signal Text</th>
                                  <th className="px-3 py-2 text-left font-medium text-gray-600">Evidence Text</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100 bg-white">
                                {report.step_reports.map((step) => (
                                  <tr key={`${report.id}-${step.lesson_step}`}>
                                    <td className="px-3 py-2 text-gray-700">{`${step.step_order}. ${step.lesson_step_title}`}</td>
                                    <td className="px-3 py-2 text-gray-700">{step.signal_submitted ? 'Submitted' : 'Not submitted'}</td>
                                    <td className="px-3 py-2 text-gray-700">{step.has_reasoning ? 'Provided' : 'Missing'}</td>
                                    <td className="px-3 py-2 text-gray-700">{step.confidence_level ?? 'n/a'}</td>
                                    <td className="px-3 py-2 text-gray-700">
                                      {step.is_correct === null ? 'n/a' : step.is_correct ? 'Correct' : 'Incorrect'}
                                    </td>
                                    <td className="px-3 py-2 text-gray-700">
                                      {step.evidence_submitted ? formatStatusLabel(step.evidence_status || 'submitted') : 'None'}
                                    </td>
                                    <td className="px-3 py-2 text-gray-700 align-top">
                                      {renderSignalTextCell(step)}
                                    </td>
                                    <td className="px-3 py-2 text-gray-700 align-top">
                                      {renderEvidenceTextCell(step)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {!loadingReports && runReports.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-6 text-center text-sm text-gray-500">
                      No run reports found for the selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {reportView === 'student_activity' && (
          <div className="mt-6 rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-4 py-3">
            <h2 className="text-sm font-semibold text-gray-900">Daily Trend</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Date</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Runs</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Participated Runs</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Signals</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Evidence</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Interventions</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Acceleration</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Engagement Avg</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {dailyReports.map((day) => (
                  <tr key={day.id}>
                    <td className="px-4 py-3 text-gray-700">{formatDate(day.report_date)}</td>
                    <td className="px-4 py-3 text-gray-700">{day.runs_count}</td>
                    <td className="px-4 py-3 text-gray-700">{participatedByDate[day.report_date] || 0}</td>
                    <td className="px-4 py-3 text-gray-700">{day.signals_submitted_count}</td>
                    <td className="px-4 py-3 text-gray-700">{day.evidence_submitted_count}</td>
                    <td className="px-4 py-3 text-gray-700">{day.intervention_count}</td>
                    <td className="px-4 py-3 text-gray-700">{day.acceleration_count}</td>
                    <td className="px-4 py-3 font-semibold text-gray-900">{Number(day.engagement_score_avg || 0).toFixed(1)}</td>
                  </tr>
                ))}
                {!loadingReports && dailyReports.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-6 text-center text-sm text-gray-500">
                      No daily report rows found for the selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          </div>
        )}
      </div>
    </OptionalAppShell>
  );
};
